import React, { useState, useMemo } from 'react';
import {
    TextField,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Grid,
    IconButton,
    Typography,
    CircularProgress,
    Alert,
    Paper,
    InputBase
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { JobHttpService } from 'cms/features/feature_job/service';
import { useQuery, useQueryClient } from 'react-query';

function ImageFieldWithGallery({ value, onChange, label = "Image URL", helperText, error, ...props }) {
    const [open, setOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [selectedImage, setSelectedImage] = useState(value || '');
    const [uploadTitle, setUploadTitle] = useState('');
    const queryClient = useQueryClient();

    const { data, isLoading, refetch } = useQuery('galleryImages', () => JobHttpService.loadGalleryImages(), {
        enabled: open, // Only fetch when dialog is open
        staleTime: 30000
    });

    const galleryImages = useMemo(() => {
        if (!data) return [];
        const images = Array.isArray(data) ? data : (data?.results || []);
        return Array.isArray(images) ? images : [];
    }, [data]);

    const handleOpen = () => {
        setOpen(true);
        setSelectedImage(value || '');
    };

    const handleClose = () => {
        setOpen(false);
        setUploadError(null);
        setUploadTitle('');
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            setUploadError('Please select an image file');
            return;
        }

        setUploading(true);
        setUploadError(null);

        try {
            const response = await JobHttpService.uploadGalleryImage(file, uploadTitle);
            // The response should have a 'url' field from the serializer
            let imageUrl = response.url;
            if (!imageUrl && response.image) {
                // Fallback: try to extract URL from image object
                imageUrl = typeof response.image === 'string' ? response.image : (response.image.url || null);
            }
            
            if (imageUrl) {
                setSelectedImage(imageUrl);
                queryClient.invalidateQueries('galleryImages');
                refetch(); // Refresh gallery list
                setUploadTitle(''); // Clear title after successful upload
            } else {
                console.error('Gallery upload response:', response);
                setUploadError('Failed to get image URL from response. Please try again.');
            }
        } catch (err) {
            setUploadError(err.message || 'Failed to upload image');
        } finally {
            setUploading(false);
            // Reset file input
            event.target.value = '';
        }
    };

    const handleSelectImage = (imageUrl) => {
        setSelectedImage(imageUrl);
    };

    const handleConfirm = () => {
        onChange(selectedImage);
        handleClose();
    };

    const handleDeleteGalleryImage = async (id, e) => {
        e.stopPropagation();
        try {
            await JobHttpService.deleteGalleryImage(id);
            queryClient.invalidateQueries('galleryImages');
            // If deleted image was selected, clear selection
            if (selectedImage && galleryImages.find(img => getImageUrl(img) === selectedImage)?.id === id) {
                setSelectedImage('');
            }
        } catch (err) {
            setUploadError(err.message || 'Failed to delete image');
        }
    };

    const getImageUrl = (image) => {
        // Priority: url field > image.url > image (if string)
        if (image.url) return image.url;
        if (image.image) {
            if (typeof image.image === 'string') return image.image;
            if (image.image.url) return image.image.url;
        }
        return null;
    };

    return (
        <>
            <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                    fullWidth
                    label={label}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    helperText={helperText}
                    error={error}
                    InputProps={{
                        readOnly: true
                    }}
                    {...props}
                />
                <Button
                    variant="outlined"
                    startIcon={<ImageIcon />}
                    onClick={handleOpen}
                    sx={{ minWidth: 120, flexShrink: 0 }}
                >
                    Browse
                </Button>
            </Box>

            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Select or Upload Image</Typography>
                        <IconButton onClick={handleClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mb: 3 }}>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                border: '2px dashed',
                                borderColor: 'divider',
                                textAlign: 'center',
                                cursor: 'pointer',
                                '&:hover': {
                                    borderColor: 'primary.main',
                                    bgcolor: 'action.hover'
                                }
                            }}
                        >
                            <input
                                accept="image/*"
                                style={{ display: 'none' }}
                                id="gallery-upload-input"
                                type="file"
                                onChange={handleFileUpload}
                                disabled={uploading}
                            />
                            <label htmlFor="gallery-upload-input">
                                <Box sx={{ cursor: uploading ? 'wait' : 'pointer' }}>
                                    <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                                    <Typography variant="body1" gutterBottom>
                                        {uploading ? 'Uploading...' : 'Click to upload new image'}
                                    </Typography>
                                    <TextField
                                        size="small"
                                        placeholder="Image title (optional)"
                                        value={uploadTitle}
                                        onChange={(e) => setUploadTitle(e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                        sx={{ mt: 1, maxWidth: 300 }}
                                    />
                                </Box>
                            </label>
                            {uploading && (
                                <CircularProgress size={24} sx={{ mt: 1 }} />
                            )}
                        </Paper>
                        {uploadError && (
                            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setUploadError(null)}>
                                {uploadError}
                            </Alert>
                        )}
                    </Box>

                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                        Select from existing images:
                    </Typography>

                    {isLoading ? (
                        <Box display="flex" justifyContent="center" py={4}>
                            <CircularProgress />
                        </Box>
                    ) : galleryImages.length === 0 ? (
                        <Alert severity="info">
                            No images in gallery. Upload an image to get started.
                        </Alert>
                    ) : (
                        <Grid container spacing={2} sx={{ maxHeight: 400, overflow: 'auto' }}>
                            {galleryImages.map((image) => {
                                const imageUrl = getImageUrl(image);
                                const isSelected = selectedImage === imageUrl;
                                return (
                                    <Grid item xs={6} sm={4} md={3} key={image.id}>
                                        <Paper
                                            elevation={isSelected ? 4 : 1}
                                            sx={{
                                                position: 'relative',
                                                cursor: 'pointer',
                                                border: isSelected ? '3px solid' : '1px solid',
                                                borderColor: isSelected ? 'primary.main' : 'divider',
                                                overflow: 'hidden',
                                                '&:hover': {
                                                    elevation: 3
                                                }
                                            }}
                                            onClick={() => handleSelectImage(imageUrl)}
                                        >
                                            {isSelected && (
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 4,
                                                        right: 4,
                                                        bgcolor: 'primary.main',
                                                        borderRadius: '50%',
                                                        width: 24,
                                                        height: 24,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        zIndex: 1
                                                    }}
                                                >
                                                    <CheckIcon sx={{ color: 'white', fontSize: 16 }} />
                                                </Box>
                                            )}
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    paddingTop: '100%',
                                                    position: 'relative',
                                                    bgcolor: 'grey.100'
                                                }}
                                            >
                                                {imageUrl && (
                                                    <Box
                                                        component="img"
                                                        src={imageUrl}
                                                        alt={image.title || 'Gallery image'}
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover'
                                                        }}
                                                        onError={(e) => {
                                                            e.target.src = '/assets/images/placeholder.png';
                                                        }}
                                                    />
                                                )}
                                                <IconButton
                                                    size="small"
                                                    sx={{
                                                        position: 'absolute',
                                                        bottom: 4,
                                                        right: 4,
                                                        bgcolor: 'error.main',
                                                        color: 'white',
                                                        '&:hover': {
                                                            bgcolor: 'error.dark'
                                                        }
                                                    }}
                                                    onClick={(e) => handleDeleteGalleryImage(image.id, e)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                            {image.title && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        p: 0.5,
                                                        display: 'block',
                                                        textAlign: 'center',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    {image.title}
                                                </Typography>
                                            )}
                                        </Paper>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}

                    {selectedImage && (
                        <Box sx={{ mt: 3, p: 2, bgcolor: 'action.selected', borderRadius: 1 }}>
                            <Typography variant="body2" fontWeight={600} gutterBottom>
                                Selected Image URL:
                            </Typography>
                            <InputBase
                                fullWidth
                                value={selectedImage}
                                readOnly
                                sx={{
                                    bgcolor: 'background.paper',
                                    p: 1,
                                    borderRadius: 1,
                                    fontFamily: 'monospace',
                                    fontSize: '0.875rem',
                                    wordBreak: 'break-all'
                                }}
                            />
                            <Box
                                component="img"
                                src={selectedImage}
                                alt="Selected preview"
                                sx={{
                                    mt: 1,
                                    maxWidth: '100%',
                                    maxHeight: 200,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    objectFit: 'contain'
                                }}
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                }}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirm}
                        disabled={!selectedImage}
                    >
                        Select Image
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default ImageFieldWithGallery;

